import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from "@/lib/api";
import { deleteWithUndo } from "@/lib/delete-with-undo";
import {
  Plus,
  Pencil,
  Eye,
  EyeOff,
  Trash2,
  X,
  Check,
  ChevronRight,
  GripVertical,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const Route = createFileRoute("/_authenticated/admin/templates")({
  component: TemplatesPage,
});

// ============================================================
// Types
// ============================================================

interface Template {
  id: string;
  orgId: string;
  highRiskType: string;
  name: string;
  description: string | null;
  permitRequired: boolean;
  requiredDocType: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  checkItemCount: number;
}

interface CheckItem {
  id: string;
  requirementId: string;
  label: string;
  itemType: string;
  options: string | null;
  isRequired: boolean;
  sortOrder: number;
  createdAt: string;
}

// ============================================================
// Constants
// ============================================================

const HIGH_RISK_TYPES = [
  { value: "crane_lift", label: "Crane / Lift" },
  { value: "confined_space", label: "Confined Space" },
  { value: "hot_work", label: "Hot Work" },
  { value: "excavation", label: "Excavation" },
  { value: "fall_protection", label: "Fall Protection" },
  { value: "lockout", label: "Lockout" },
] as const;

const CHECK_ITEM_TYPES = [
  { value: "checkbox", label: "Checkbox" },
  { value: "person", label: "Person" },
  { value: "reading_set", label: "Reading Set" },
  { value: "select", label: "Select" },
  { value: "number", label: "Number" },
  { value: "text", label: "Text" },
] as const;

const TYPE_COLORS: Record<string, string> = {
  crane_lift: "bg-blue-100 text-blue-800",
  confined_space: "bg-orange-100 text-orange-800",
  hot_work: "bg-red-100 text-red-800",
  excavation: "bg-amber-100 text-amber-800",
  fall_protection: "bg-purple-100 text-purple-800",
  lockout: "bg-gray-100 text-gray-800",
};

function getTypeLabel(value: string) {
  return HIGH_RISK_TYPES.find((t) => t.value === value)?.label ?? value;
}

function getItemTypeLabel(value: string) {
  return CHECK_ITEM_TYPES.find((t) => t.value === value)?.label ?? value;
}

// ============================================================
// Main Page Component
// ============================================================

function TemplatesPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ["admin", "templates"],
    queryFn: () => apiGet<{ data: Template[] }>("/api/admin/templates"),
  });

  const templates = templatesData?.data ?? [];
  const selectedTemplate = templates.find((t) => t.id === selectedId);

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => apiPatch(`/api/admin/templates/${id}/active`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "templates"] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to toggle active status");
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2 py-8">
        <div className="h-4 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
        <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Templates</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Manage FLHA and toolbox talk templates.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left panel: Template list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Templates
            </h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Template
            </Button>
          </div>

          {showCreateForm && (
            <TemplateCreateForm
              onClose={() => setShowCreateForm(false)}
            />
          )}

          {templates.length === 0 && !showCreateForm && (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No templates yet. Click "Add Template" to create one.
            </p>
          )}

          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={selectedId === template.id}
              onSelect={() =>
                setSelectedId(
                  selectedId === template.id ? null : template.id
                )
              }
              onToggleActive={() =>
                toggleActiveMutation.mutate(template.id)
              }
            />
          ))}
        </div>

        {/* Right panel: Check items for selected template */}
        <div>
          {selectedTemplate ? (
            <CheckItemsPanel template={selectedTemplate} />
          ) : (
            <div className="flex items-center justify-center h-48 border border-dashed rounded-lg">
              <p className="text-sm text-muted-foreground">
                Select a template to view its check items
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Template Card
// ============================================================

function TemplateCard({
  template,
  isSelected,
  onSelect,
  onToggleActive,
}: {
  template: Template;
  isSelected: boolean;
  onSelect: () => void;
  onToggleActive: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <TemplateEditForm
        template={template}
        onClose={() => setIsEditing(false)}
      />
    );
  }

  return (
    <Card
      className={cn(
        "cursor-pointer transition-colors",
        isSelected && "ring-2 ring-primary"
      )}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">
                {template.name}
              </span>
              <Badge
                className={cn(
                  "text-xs px-1.5 py-0",
                  TYPE_COLORS[template.highRiskType]
                )}
              >
                {getTypeLabel(template.highRiskType)}
              </Badge>
              {!template.isActive && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  Inactive
                </Badge>
              )}
            </div>
            {template.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {template.description}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span>
                {template.checkItemCount} check item
                {template.checkItemCount !== 1 ? "s" : ""}
              </span>
              {template.permitRequired && (
                <span className="text-orange-600 font-medium">
                  Permit required
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              title="Edit template"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onToggleActive();
              }}
              title={template.isActive ? "Deactivate" : "Activate"}
            >
              {template.isActive ? (
                <Eye className="h-3.5 w-3.5" />
              ) : (
                <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </Button>
            <ChevronRight
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                isSelected && "rotate-90"
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Template Create Form
// ============================================================

function TemplateCreateForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [highRiskType, setHighRiskType] = useState("");
  const [description, setDescription] = useState("");
  const [permitRequired, setPermitRequired] = useState(false);
  const [requiredDocType, setRequiredDocType] = useState("");

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost("/api/admin/templates", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "templates"] });
      toast.success("Template created");
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to create template");
    },
  });

  const handleSubmit = () => {
    if (!name.trim() || !highRiskType) {
      toast.error("Name and high-risk type are required");
      return;
    }
    createMutation.mutate({
      name: name.trim(),
      highRiskType,
      description: description.trim() || undefined,
      permitRequired,
      requiredDocType: requiredDocType.trim() || null,
    });
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">New Template</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Template name"
              className="h-9 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">High-Risk Type *</Label>
            <Select value={highRiskType} onValueChange={setHighRiskType}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {HIGH_RISK_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={2}
            className="text-sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="create-permit"
              checked={permitRequired}
              onCheckedChange={(v) => setPermitRequired(v === true)}
            />
            <Label htmlFor="create-permit" className="text-xs">
              Permit required
            </Label>
          </div>
          <div>
            <Label className="text-xs">Required Doc Type</Label>
            <Input
              value={requiredDocType}
              onChange={(e) => setRequiredDocType(e.target.value)}
              placeholder="e.g., Permit"
              className="h-9 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Template Edit Form
// ============================================================

function TemplateEditForm({
  template,
  onClose,
}: {
  template: Template;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? "");
  const [permitRequired, setPermitRequired] = useState(template.permitRequired);
  const [requiredDocType, setRequiredDocType] = useState(
    template.requiredDocType ?? ""
  );

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPut(`/api/admin/templates/${template.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "templates"] });
      toast.success("Template updated");
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update template");
    },
  });

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    updateMutation.mutate({
      name: name.trim(),
      description: description.trim() || null,
      permitRequired,
      requiredDocType: requiredDocType.trim() || null,
    });
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Edit Template</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div>
          <Label className="text-xs">Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-9 text-sm"
          />
        </div>

        <div>
          <Label className="text-xs">Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="text-sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="edit-permit"
              checked={permitRequired}
              onCheckedChange={(v) => setPermitRequired(v === true)}
            />
            <Label htmlFor="edit-permit" className="text-xs">
              Permit required
            </Label>
          </div>
          <div>
            <Label className="text-xs">Required Doc Type</Label>
            <Input
              value={requiredDocType}
              onChange={(e) => setRequiredDocType(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Check Items Panel
// ============================================================

function CheckItemsPanel({ template }: { template: Template }) {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [localItems, setLocalItems] = useState<CheckItem[] | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Fetch check items
  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["admin", "templates", template.id, "items"],
    queryFn: () =>
      apiGet<{ data: CheckItem[] }>(
        `/api/admin/templates/${template.id}/items`
      ),
  });

  const items = localItems ?? itemsData?.data ?? [];

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (itemIds: string[]) =>
      apiPost(`/api/admin/templates/${template.id}/items/reorder`, { itemIds }),
    onSuccess: () => {
      setLocalItems(null);
      queryClient.invalidateQueries({
        queryKey: ["admin", "templates", template.id, "items"],
      });
    },
    onError: (err: Error) => {
      setLocalItems(null);
      toast.error(err.message || "Failed to reorder items");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (itemId: string) =>
      apiDelete(`/api/admin/templates/${template.id}/items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "templates", template.id, "items"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "templates"] });
      toast.success("Check item deleted");
    },
    onError: (err: Error) => {
      toast.error(
        err.message || "Failed to delete check item"
      );
    },
  });

  const handleDelete = (itemId: string, label?: string) => {
    deleteWithUndo(label ?? "check item", () => deleteMutation.mutateAsync(itemId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);
    setLocalItems(newItems);
    reorderMutation.mutate(newItems.map((i) => i.id));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Check Items
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {template.name} -- drag to reorder
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </div>

      {showCreateForm && (
        <CheckItemCreateForm
          templateId={template.id}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      {isLoading ? (
        <div className="space-y-2 py-4">
          <div className="h-4 bg-muted rounded animate-pulse" />
          <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
        </div>
      ) : items.length === 0 && !showCreateForm ? (
        <div className="flex items-center justify-center h-32 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground">
            No check items yet
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {items.map((item) =>
                editingItemId === item.id ? (
                  <CheckItemEditForm
                    key={item.id}
                    item={item}
                    templateId={template.id}
                    onClose={() => setEditingItemId(null)}
                  />
                ) : (
                  <SortableCheckItemRow
                    key={item.id}
                    item={item}
                    onEdit={() => setEditingItemId(item.id)}
                    onDelete={() => handleDelete(item.id, item.label)}
                  />
                )
              )}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ============================================================
// Check Item Row
// ============================================================

function SortableCheckItemRow({
  item,
  onEdit,
  onDelete,
}: {
  item: CheckItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style}>
      <CardContent className="p-3 flex items-center gap-2">
        <button
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{item.label}</span>
            <Badge
              variant="secondary"
              className="text-xs px-1.5 py-0 cursor-default"
              title="Type cannot be changed after creation"
            >
              {getItemTypeLabel(item.itemType)}
            </Badge>
            {item.isRequired && (
              <Badge
                variant="secondary"
                className="text-xs px-1.5 py-0 bg-blue-50 text-blue-700"
              >
                Required
              </Badge>
            )}
          </div>
          {item.options && item.itemType === "select" && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              Options: {item.options}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={onEdit}
            title="Edit"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Check Item Create Form
// ============================================================

function CheckItemCreateForm({
  templateId,
  onClose,
}: {
  templateId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [itemType, setItemType] = useState("");
  const [options, setOptions] = useState("");
  const [isRequired, setIsRequired] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/api/admin/templates/${templateId}/items`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "templates", templateId, "items"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "templates"] });
      toast.success("Check item created");
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to create check item");
    },
  });

  const handleSubmit = () => {
    if (!label.trim() || !itemType) {
      toast.error("Label and item type are required");
      return;
    }
    createMutation.mutate({
      label: label.trim(),
      itemType,
      options: itemType === "select" ? options.trim() || null : null,
      isRequired,
      sortOrder,
    });
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">New Check Item</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Label *</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Check item label"
              className="h-9 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Type *</Label>
            <Select value={itemType} onValueChange={setItemType}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {CHECK_ITEM_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {itemType === "select" && (
          <div>
            <Label className="text-xs">Options (comma-separated)</Label>
            <Input
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              placeholder='e.g., "Yes,No,N/A"'
              className="h-9 text-sm"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="create-item-required"
              checked={isRequired}
              onCheckedChange={(v) => setIsRequired(v === true)}
            />
            <Label htmlFor="create-item-required" className="text-xs">
              Required
            </Label>
          </div>
          <div>
            <Label className="text-xs">Sort Order</Label>
            <Input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              className="h-9 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Check Item Edit Form
// ============================================================

function CheckItemEditForm({
  item,
  templateId,
  onClose,
}: {
  item: CheckItem;
  templateId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(item.label);
  const [options, setOptions] = useState(item.options ?? "");
  const [isRequired, setIsRequired] = useState(item.isRequired);

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPut(`/api/admin/templates/${templateId}/items/${item.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "templates", templateId, "items"],
      });
      toast.success("Check item updated");
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update check item");
    },
  });

  const handleSubmit = () => {
    if (!label.trim()) {
      toast.error("Label is required");
      return;
    }
    updateMutation.mutate({
      label: label.trim(),
      options: item.itemType === "select" ? options.trim() || null : null,
      isRequired,
    });
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Edit Check Item</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Label *</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Type (locked)</Label>
            <div className="h-9 flex items-center">
              <Badge variant="secondary" className="text-xs">
                {getItemTypeLabel(item.itemType)}
              </Badge>
            </div>
          </div>
        </div>

        {item.itemType === "select" && (
          <div>
            <Label className="text-xs">Options (comma-separated)</Label>
            <Input
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <Checkbox
            id={`edit-item-required-${item.id}`}
            checked={isRequired}
            onCheckedChange={(v) => setIsRequired(v === true)}
          />
          <Label htmlFor={`edit-item-required-${item.id}`} className="text-xs">
            Required
          </Label>
        </div>

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
